import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Typography, Box, Card, Grid, Button, Chip, alpha, useTheme } from '@mui/material';
import {
  ArrowForward, InsightsOutlined, FormatListBulleted, AddCircleOutline, MoveToInboxOutlined,
  PeopleAltOutlined, ManageAccountsOutlined, GavelOutlined, BookmarkBorderOutlined,
  SendAndArchiveOutlined, PersonOutline, WorkspacePremiumOutlined, CalendarTodayOutlined,
} from '@mui/icons-material';
import { jwtDecode } from 'jwt-decode';
import LogoBlanco from '../assets/Alerta Normativa-logo2026-blanco.png';
import { brand, heroGradient } from '../styles/theme';
import { useModulosUsuario } from '../hooks/useModulosUsuario';

const leerUsuario = () => {
  try {
    const decoded = jwtDecode(localStorage.getItem('token'));
    return { nombre: decoded.given_name || '', rol: decoded.rol || '' };
  } catch {
    return { nombre: '', rol: '' };
  }
};

const saludoSegunHora = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return 'Buenos días';
  if (h >= 12 && h < 20) return 'Buenas tardes';
  return 'Buenas noches';
};

const fechaHoy = () => {
  const f = new Date().toLocaleDateString('es-CL', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  return f.charAt(0).toUpperCase() + f.slice(1);
};

// Accesos rápidos por rol. `servicio` condiciona el acceso a un módulo contratado.
const ACCESOS = {
  Administrador: [
    { to: '/dashboard-impacto', icon: <InsightsOutlined />, titulo: 'Dashboard de impacto', desc: 'Indicadores y distribución del impacto normativo.', tono: 'primary' },
    { to: '/gestor-normativa', icon: <FormatListBulleted />, titulo: 'Gestor de normativas', desc: 'Revisa, edita y administra las normativas publicadas.', tono: 'teal' },
    { to: '/nueva-normativa', icon: <AddCircleOutline />, titulo: 'Agregar normativa', desc: 'Registra una nueva normativa en la plataforma.', tono: 'navy' },
    { to: '/bandeja-normativas', icon: <MoveToInboxOutlined />, titulo: 'Bandeja de normativas', desc: 'Normativas detectadas automáticamente por el scraper.', tono: 'primary' },
    { to: '/cliente/gestor-clientes', icon: <PeopleAltOutlined />, titulo: 'Gestor de clientes', desc: 'Administra clientes, organismos y rubros asociados.', tono: 'teal' },
    { to: '/usuarios', icon: <ManageAccountsOutlined />, titulo: 'Usuarios', desc: 'Gestiona los accesos y roles de los usuarios.', tono: 'navy' },
  ],
  Usuario: [
    { to: '/dashboard-impacto', icon: <InsightsOutlined />, titulo: 'Dashboard de impacto', desc: 'Indicadores y distribución del impacto normativo.', tono: 'primary', servicio: 'Impacto' },
    { to: '/usuario/gestor-normativa', icon: <GavelOutlined />, titulo: 'Normativas históricas', desc: 'Consulta las normativas enviadas a tu organización.', tono: 'teal' },
    { to: '/usuario/normativas-guardadas', icon: <BookmarkBorderOutlined />, titulo: 'Guardados', desc: 'Accede a las normativas que marcaste para revisar.', tono: 'navy' },
    { to: '/usuario/integracion-grc-normativas', icon: <SendAndArchiveOutlined />, titulo: 'Enviar a AustranetGRC', desc: 'Integra normativas con tu plataforma AustranetGRC.', tono: 'primary', servicio: 'Integracion AustranetGRC' },
    { to: '/perfil-usuario', icon: <PersonOutline />, titulo: 'Perfil de usuario', desc: 'Revisa tus datos y preferencias de cuenta.', tono: 'teal' },
  ],
  Suscriptor: [
    { to: '/suscriptor/bandeja', icon: <GavelOutlined />, titulo: 'Mis normativas', desc: 'Revisa las normativas de tu suscripción.', tono: 'primary' },
    { to: '/suscriptor/mi-suscripcion', icon: <WorkspacePremiumOutlined />, titulo: 'Mi suscripción', desc: 'Consulta el detalle y estado de tu plan.', tono: 'teal' },
  ],
};

const ACCION_PRINCIPAL = {
  Administrador: { to: '/gestor-normativa', label: 'Ir al gestor de normativas' },
  Usuario: { to: '/usuario/gestor-normativa', label: 'Ver normativas' },
  Suscriptor: { to: '/suscriptor/bandeja', label: 'Ver mis normativas' },
};

const AccesoCard = ({ to, icon, titulo, desc, color }) => (
  <Card
    component={Link}
    to={to}
    elevation={1}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      p: 2.5,
      textDecoration: 'none',
      color: 'inherit',
      transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: (t) => t.shadows[4],
        borderColor: alpha(color, 0.4),
      },
      '&:hover .acceso-flecha': { transform: 'translateX(3px)', color },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box
        sx={{
          width: 46, height: 46, borderRadius: 3,
          display: 'grid', placeItems: 'center',
          bgcolor: alpha(color, 0.12), color,
        }}
      >
        {icon}
      </Box>
      <ArrowForward
        className="acceso-flecha"
        fontSize="small"
        sx={{ color: 'text.disabled', transition: 'transform .2s, color .2s' }}
      />
    </Box>
    <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 700, lineHeight: 1.3 }}>
      {titulo}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
      {desc}
    </Typography>
  </Card>
);

const Home = () => {
  const theme = useTheme();
  const usuario = useMemo(leerUsuario, []);
  const { tieneServicio } = useModulosUsuario();

  const tonos = {
    primary: theme.palette.primary.main,
    teal: brand.teal,
    navy: theme.palette.mode === 'dark' ? '#8FA3B8' : brand.navy,
  };

  const accesos = (ACCESOS[usuario.rol] || ACCESOS.Usuario).filter(
    (a) => !a.servicio || tieneServicio(a.servicio)
  );
  const accion = ACCION_PRINCIPAL[usuario.rol] || ACCION_PRINCIPAL.Usuario;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pt: { xs: 1, sm: 0.5 }, pb: 4 }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 5,
          px: { xs: 3, md: 5 },
          py: { xs: 3.5, md: 5 },
          color: '#fff',
          background: heroGradient,
          boxShadow: `0 18px 40px -18px ${alpha(brand.greenDark, 0.55)}`,
          // Trama de puntos decorativa
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.16) 1px, transparent 1px)',
            backgroundSize: '18px 18px',
            maskImage: 'linear-gradient(90deg, transparent 35%, #000 100%)',
            WebkitMaskImage: 'linear-gradient(90deg, transparent 35%, #000 100%)',
          },
          // Halo de luz
          '&::after': {
            content: '""',
            position: 'absolute',
            width: 420, height: 420,
            right: -120, top: -180,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.18), transparent 65%)',
          },
        }}
      >
        <Grid container spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} md={8}>
            <Chip
              icon={<CalendarTodayOutlined sx={{ fontSize: 15 }} />}
              label={fechaHoy()}
              size="small"
              sx={{
                mb: 2,
                color: '#fff',
                bgcolor: 'rgba(255,255,255,0.14)',
                border: '1px solid rgba(255,255,255,0.22)',
                backdropFilter: 'blur(6px)',
                '& .MuiChip-icon': { color: 'rgba(255,255,255,0.85)' },
              }}
            />
            <Typography
              variant="h3"
              sx={{ fontSize: { xs: '1.75rem', sm: '2.1rem', md: '2.4rem' }, lineHeight: 1.15 }}
            >
              {saludoSegunHora()}{usuario.nombre ? `, ${usuario.nombre}` : ''}
            </Typography>
            <Typography sx={{ mt: 1.5, opacity: 0.88, maxWidth: 560, fontSize: { xs: '0.95rem', md: '1.02rem' } }}>
              Bienvenido/a a Alerta Normativa Global. Mantente al día con los cambios
              regulatorios y accede rápidamente a las principales secciones de la plataforma.
            </Typography>
            <Button
              component={Link}
              to={accion.to}
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{
                mt: 3,
                px: 2.5,
                py: 1,
                borderRadius: 999,
                bgcolor: '#fff',
                color: brand.greenDark,
                boxShadow: '0 6px 18px -6px rgba(0,0,0,0.35)',
                '&:hover': { bgcolor: '#F1F7F3', boxShadow: '0 8px 22px -6px rgba(0,0,0,0.4)' },
              }}
            >
              {accion.label}
            </Button>
          </Grid>
          <Grid item md={4} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            <Box
              component="img"
              src={LogoBlanco}
              alt="Alerta Normativa Global"
              sx={{ width: '100%', maxWidth: 240, opacity: 0.95, filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.18))' }}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h6">Accesos rápidos</Typography>
          <Typography variant="body2" color="text.secondary">
            Las secciones que más utilizas, a un clic de distancia.
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={2.5}>
        {accesos.map((a) => (
          <Grid item xs={12} sm={6} lg={4} key={a.to}>
            <AccesoCard {...a} color={tonos[a.tono]} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Home;
